import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';
import { TabBar } from '@/shared-ui/TabBar';
import { ErrorBoundary } from '@/shared-ui/ErrorBoundary';
import { LoginPage } from '@/features/auth/LoginPage';
import { HomeView } from '@/features/home/HomeView';

const PendingTaskPage = lazy(() =>
  import('@/features/note-detail/PendingTaskPage').then((m) => ({ default: m.PendingTaskPage }))
);
const NoteDetailPage = lazy(() =>
  import('@/features/note-detail/NoteDetailPage').then((m) => ({ default: m.NoteDetailPage }))
);
const TodoHubView = lazy(() =>
  import('@/features/todo-hub/TodoHubView').then((m) => ({ default: m.TodoHubView }))
);
const TodoDetailPage = lazy(() =>
  import('@/features/todo-hub/TodoDetailPage').then((m) => ({ default: m.TodoDetailPage }))
);
const OrganizeView = lazy(() =>
  import('@/features/organize/OrganizeView').then((m) => ({ default: m.OrganizeView }))
);
const ProfileView = lazy(() =>
  import('@/features/profile/ProfileView').then((m) => ({ default: m.ProfileView }))
);
const SecurityPage = lazy(() =>
  import('@/features/profile/pages/SecurityPage').then((m) => ({ default: m.SecurityPage }))
);
const SettingsPage = lazy(() =>
  import('@/features/profile/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const TrashPage = lazy(() =>
  import('@/features/profile/pages/TrashPage').then((m) => ({ default: m.TrashPage }))
);
const MembershipPage = lazy(() =>
  import('@/features/membership/MembershipPage').then((m) => ({ default: m.MembershipPage }))
);
const PaymentResultPage = lazy(() =>
  import('@/features/membership/PaymentResultPage').then((m) => ({ default: m.PaymentResultPage }))
);

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-fg-muted text-sm">
      加载中…
    </div>
  );
}

function lazyRoute(node: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>{node}</Suspense>
    </ErrorBoundary>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  if (status === 'idle' || status === 'loading') {
    return <div className="min-h-screen flex items-center justify-center text-fg-muted">加载中…</div>;
  }
  if (status === 'guest') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  if (status === 'authed') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppShell() {
  return (
    <RequireAuth>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      <TabBar />
    </RequireAuth>
  );
}

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <RedirectIfAuthed>
        <LoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    element: <AppShell />,
    children: [
      { path: '/', element: <HomeView /> },
      { path: '/notes/:id', element: lazyRoute(<NoteDetailPage />) },
      { path: '/tasks/:taskId', element: lazyRoute(<PendingTaskPage />) },
      { path: '/todos', element: lazyRoute(<TodoHubView />) },
      { path: '/todos/:id', element: lazyRoute(<TodoDetailPage />) },
      { path: '/organize', element: lazyRoute(<OrganizeView />) },
      { path: '/profile', element: lazyRoute(<ProfileView />) },
      { path: '/profile/security', element: lazyRoute(<SecurityPage />) },
      { path: '/profile/settings', element: lazyRoute(<SettingsPage />) },
      { path: '/profile/trash', element: lazyRoute(<TrashPage />) },
      { path: '/membership', element: lazyRoute(<MembershipPage />) },
      { path: '/membership/result', element: lazyRoute(<PaymentResultPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];
